import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Loader2, ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { tradieLogin, tradieSetPassword } from '@/lib/api';

const TradieLoginPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [setupData, setSetupData] = useState({ email: '', password: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await tradieLogin(loginData.email, loginData.password);
      localStorage.setItem('tradieToken', response.token);
      localStorage.setItem('tradieEmail', response.email);
      localStorage.setItem('tradieBusinessName', response.business_name);
      toast.success('Welcome back!');
      navigate('/tradie/portal');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      if (message.includes('Password not set')) {
        toast.error('Please set your password first', {
          description: 'Switch to the "Set Password" tab'
        });
        setActiveTab('setup');
        setSetupData({ ...setupData, email: loginData.email });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (setupData.password !== setupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (setupData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await tradieSetPassword(setupData.email, setupData.password);
      toast.success('Password set successfully!', {
        description: 'You can now log in with your credentials'
      });
      setActiveTab('login');
      setLoginData({ email: setupData.email, password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="tradie-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Directory
        </Link>
        
        <Card>
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <User className="w-7 h-7 text-primary" />
            </div>
            <CardTitle className="font-heading text-2xl">Tradie Portal</CardTitle>
            <CardDescription>
              Manage your listing, view enquiries, and subscription
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="setup">Set Password</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="tradie-login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email Address</Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      data-testid="tradie-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="••••••••"
                        required
                        data-testid="tradie-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={loading}
                    data-testid="tradie-login-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="setup">
                <form onSubmit={handleSetPassword} className="space-y-4" data-testid="tradie-setup-form">
                  <div className="bg-accent/50 rounded-lg p-3 text-sm text-accent-foreground mb-4">
                    <KeyRound className="w-4 h-4 inline mr-2" />
                    First time? Set your password using your registered email.
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="setup-email">Registered Email</Label>
                    <Input
                      id="setup-email"
                      type="email"
                      value={setupData.email}
                      onChange={(e) => setSetupData({ ...setupData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      data-testid="setup-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="setup-password">New Password</Label>
                    <Input
                      id="setup-password"
                      type="password"
                      value={setupData.password}
                      onChange={(e) => setSetupData({ ...setupData, password: e.target.value })}
                      placeholder="Min 8 characters"
                      required
                      data-testid="setup-password"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="setup-confirm">Confirm Password</Label>
                    <Input
                      id="setup-confirm"
                      type="password"
                      value={setupData.confirmPassword}
                      onChange={(e) => setSetupData({ ...setupData, confirmPassword: e.target.value })}
                      placeholder="Confirm password"
                      required
                      data-testid="setup-confirm"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90"
                    disabled={loading}
                    data-testid="setup-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Setting password...
                      </>
                    ) : (
                      'Set Password'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            <div className="mt-6 pt-6 border-t text-center text-sm text-muted-foreground">
              <p>Don't have an account?</p>
              <Link to="/register" className="text-primary hover:underline font-medium">
                Register your business
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TradieLoginPage;
